import { useState } from 'react';
import { supabase } from '../supabaseClient';

const T = {
  en: {
    title: 'GFI Nexus',
    subtitle: 'Operations Platform',
    email: 'Username',
    password: 'Password',
    signIn: 'Sign In',
    signingIn: 'Signing in…',
    emailPh: 'username',
    passwordPh: '••••••••',
    invalidCreds: 'Invalid credentials.',
    missing: 'Please enter username and password.',
    missingEmail: 'Please enter your username.',
    footer: 'Ground Fresh International Inc.',
    forgot: 'Forgot password?',
    recoverTitle: 'Reset password',
    recoverSubtitle: 'Enter your email and we will send you a recovery link.',
    sendRecovery: 'Send recovery link',
    sending: 'Sending…',
    recoveryError: 'Could not send the recovery link. Please try again.',
    recoverySent: 'Recovery link sent to your email.',
    backToSignIn: '← Back to sign in',
  },
  es: {
    title: 'GFI Nexus',
    subtitle: 'Plataforma de Operaciones',
    email: 'Usuario',
    password: 'Contraseña',
    signIn: 'Iniciar Sesión',
    signingIn: 'Iniciando sesión…',
    emailPh: 'usuario',
    passwordPh: '••••••••',
    invalidCreds: 'Credenciales inválidas.',
    missing: 'Ingresa usuario y contraseña.',
    missingEmail: 'Ingresa tu usuario.',
    footer: 'Ground Fresh International Inc.',
    forgot: '¿Olvidaste tu contraseña?',
    recoverTitle: 'Recuperar contraseña',
    recoverSubtitle: 'Ingresa tu correo y te enviaremos un link de recuperación.',
    sendRecovery: 'Enviar link de recuperación',
    sending: 'Enviando…',
    recoveryError: 'No se pudo enviar el link. Inténtalo de nuevo.',
    recoverySent: 'Se envió un link a tu email.',
    backToSignIn: '← Volver a iniciar sesión',
  },
};

const NAVY = '#0D1B2A';
const NAVY_2 = '#152A40';
const ACCENT = '#5BBCB0';
const ACCENT_DARK = '#3D9B8F';

function LoginLogo() {
  return (
    <svg width="96" height="96" viewBox="-55 -55 110 110" style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id="loginLogoGl" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.25)" />
          <stop offset="40%" stopColor="rgba(255,255,255,0.05)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.15)" />
        </linearGradient>
      </defs>
      <rect x="-35" y="-35" width="70" height="70" rx="16" fill="#1B3A35" />
      <rect x="-35" y="-35" width="70" height="70" rx="16" fill="url(#loginLogoGl)" />
      <text x="0" y="2" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="22" fontWeight="700" fill={ACCENT} letterSpacing="3">GFI</text>
      <rect x="-16" y="10" width="32" height="1.2" rx="0.6" fill={ACCENT_DARK} />
      <text x="0" y="22" textAnchor="middle" fontFamily="'DM Sans', sans-serif" fontSize="8" fontWeight="500" fill={ACCENT_DARK} letterSpacing="3">NEXUS</text>
    </svg>
  );
}

export default function LoginPage({ lang, setLang }) {
  const t = T[lang] || T.es;
  const [mode, setMode] = useState('signin'); // 'signin' | 'recover'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sentTo, setSentTo] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setSentTo('');
    if (next === 'signin') setPassword('');
  };

  const onSignIn = async (e) => {
    e.preventDefault();
    setError('');
    const input = email.trim();
    if (!input || !password) {
      setError(t.missing);
      return;
    }
    setBusy(true);
    try {
      /* If input contains @, treat as email (admin path). Otherwise
         resolve the username to its masked_email via our Netlify
         Function and sign in with that. */
      let signInEmail = input;
      if (!input.includes('@')) {
        const res = await fetch('/.netlify/functions/resolve-username', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: input.toLowerCase() }),
        });
        const data = await res.json().catch(function () { return {}; });
        if (!res.ok || !data.success) {
          setError(t.invalidCreds);
          setBusy(false);
          return;
        }
        signInEmail = data.maskedEmail;
      }

      const { error: authErr } = await supabase.auth.signInWithPassword({
        email: signInEmail,
        password,
      });
      if (authErr) {
        setError(t.invalidCreds);
        setBusy(false);
        return;
      }
      // On success, App.js auth listener swaps to the main shell.
    } catch (err) {
      setError(t.invalidCreds);
      setBusy(false);
    }
  };

  const onRecover = async (e) => {
    e.preventDefault();
    setError('');
    const input = email.trim();
    if (!input) {
      setError(t.missingEmail);
      return;
    }
    setBusy(true);
    try {
      /* Always send the username (the part before @ if email was
         entered, or the raw username otherwise) to our reset
         endpoint. The backend resolves it to the real personal
         email and sends the link via Resend. */
      const username = input.includes('@') ? input.split('@')[0].toLowerCase() : input.toLowerCase();
      const res = await fetch('/.netlify/functions/send-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: username }),
      });
      const data = await res.json().catch(function () { return {}; });
      if (!res.ok) {
        setError(data.error || ('HTTP ' + res.status));
        setBusy(false);
        return;
      }
      setSentTo(input);
      setBusy(false);
    } catch (err) {
      setError(err.message || String(err));
      setBusy(false);
    }
  };

  const inp = {
    width: '100%',
    padding: '11px 14px',
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.18)',
    background: 'rgba(255,255,255,0.06)',
    color: '#fff',
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
    transition: 'border-color 0.15s, background 0.15s',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(255,255,255,0.75)',
    marginBottom: 6,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
  };

  const linkBtn = {
    background: 'transparent',
    border: 'none',
    color: ACCENT,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '4px 8px',
    textDecoration: 'none',
    letterSpacing: '0.02em',
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_2} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative glow */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: '-20%',
          right: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${ACCENT}22 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          bottom: '-20%',
          left: '-10%',
          width: 500,
          height: 500,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${ACCENT_DARK}22 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Language toggle */}
      {typeof setLang === 'function' && (
        <div
          style={{
            position: 'absolute',
            top: 20,
            right: 20,
            display: 'flex',
            gap: 4,
            padding: 3,
            borderRadius: 8,
            background: 'rgba(255,255,255,0.08)',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          {['en', 'es'].map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              style={{
                padding: '4px 10px',
                borderRadius: 5,
                border: 'none',
                background: lang === l ? ACCENT : 'transparent',
                color: lang === l ? NAVY : 'rgba(255,255,255,0.7)',
                fontSize: 11,
                fontWeight: 700,
                cursor: 'pointer',
                fontFamily: 'inherit',
                letterSpacing: '0.05em',
              }}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={mode === 'signin' ? onSignIn : onRecover}
        style={{
          width: '100%',
          maxWidth: 400,
          padding: '40px 36px',
          borderRadius: 16,
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.10)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
          <LoginLogo />
          <h1
            style={{
              margin: '18px 0 4px',
              color: '#fff',
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: '0.02em',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            {mode === 'signin' ? t.title : t.recoverTitle}
          </h1>
          <p
            style={{
              margin: 0,
              color: 'rgba(255,255,255,0.6)',
              fontSize: mode === 'signin' ? 12 : 12,
              fontWeight: 500,
              letterSpacing: mode === 'signin' ? '0.08em' : '0.01em',
              textTransform: mode === 'signin' ? 'uppercase' : 'none',
              textAlign: 'center',
              lineHeight: 1.45,
              maxWidth: 300,
            }}
          >
            {mode === 'signin' ? t.subtitle : t.recoverSubtitle}
          </p>
        </div>

        {/* Success state for recovery */}
        {mode === 'recover' && sentTo ? (
          <>
            <div
              role="status"
              style={{
                padding: '14px 16px',
                borderRadius: 8,
                background: 'rgba(91,188,176,0.12)',
                border: '1px solid rgba(91,188,176,0.4)',
                color: ACCENT,
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 18,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              <div style={{ fontSize: 22, marginBottom: 6 }}>✓</div>
              {t.recoverySent}
              <div style={{ marginTop: 6, fontSize: 11, color: 'rgba(255,255,255,0.6)', fontWeight: 500, fontFamily: "'JetBrains Mono', monospace" }}>
                {sentTo}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <button type="button" onClick={() => switchMode('signin')} style={linkBtn}>
                {t.backToSignIn}
              </button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: mode === 'signin' ? 14 : 18 }}>
              <label htmlFor="login-email" style={labelStyle}>{t.email}</label>
              <input
                id="login-email"
                type="text"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.emailPh}
                disabled={busy}
                style={inp}
              />
            </div>

            {mode === 'signin' && (
              <div style={{ marginBottom: 18 }}>
                <label htmlFor="login-password" style={labelStyle}>{t.password}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t.passwordPh}
                    disabled={busy}
                    style={{ ...inp, paddingRight: 42 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    tabIndex={-1}
                    style={{
                      position: 'absolute', right: 10, top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent', border: 'none',
                      padding: 4, cursor: 'pointer',
                      color: 'rgba(255,255,255,0.55)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            )}

            {error && (
              <div
                role="alert"
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  background: 'rgba(220,38,38,0.15)',
                  border: '1px solid rgba(220,38,38,0.4)',
                  color: '#FCA5A5',
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 14,
                  textAlign: 'center',
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: 'none',
                background: busy ? ACCENT_DARK : ACCENT,
                color: NAVY,
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: '0.03em',
                cursor: busy ? 'wait' : 'pointer',
                fontFamily: 'inherit',
                transition: 'background 0.15s, transform 0.05s',
                boxShadow: '0 4px 14px rgba(91,188,176,0.25)',
              }}
              onMouseDown={(e) => { if (!busy) e.currentTarget.style.transform = 'translateY(1px)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = ''; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
            >
              {mode === 'signin'
                ? (busy ? t.signingIn : t.signIn)
                : (busy ? t.sending : t.sendRecovery)}
            </button>

            <div style={{ marginTop: 14, textAlign: 'center' }}>
              {mode === 'signin' ? (
                <button type="button" onClick={() => switchMode('recover')} style={linkBtn}>
                  {t.forgot}
                </button>
              ) : (
                <button type="button" onClick={() => switchMode('signin')} style={linkBtn}>
                  {t.backToSignIn}
                </button>
              )}
            </div>
          </>
        )}

        <div
          style={{
            marginTop: 24,
            textAlign: 'center',
            fontSize: 10,
            color: 'rgba(255,255,255,0.4)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
          }}
        >
          {t.footer}
        </div>
      </form>
    </div>
  );
}
